import type { CropConfiguration } from "./types.js";

// Suggestions only: growers can enter any cultivar, rootstock or clonal selection.
export const CropConfigurations = {
    grape: {
        hasRootstock: true,
        knownRootstocks: [
            "Castel 188-15",
            "Champanel",
            "Cosmo 10",
            "Cosmo 2",
            "Couderc 1202",
            "Couderc 1613",
            "Couderc 161-49",
            "Couderc 1616",
            "Couderc 1616E",
            "Dog Ridge",
            "Fercal",
            "Florilush",
            "Freedom",
            "Harmony",
            "IAC 572",
            "IAC 766",
            "Kober 125AA",
            "Kober 5BB",
            "Millardet et de Grasset 219A",
            "Millardet et de Grasset 41B",
            "Richter 99",
            "Riparia Gloire",
            "Salt Creek",
            "Schwarzmann",
            "St. George",
            "Tampa",
            "Teleki 5C",
            "Teleki 8B",
            "039-16",
            "101-14 Mgt",
            "1103P",
            "110R",
            "140 Ruggeri",
            "3309C",
            "420A",
            "SO4",
        ],
        hasClonalSelection: true,
        defaultColor: "#8a62c7",
        customName: false,
    },
    apple: {
        hasRootstock: true,
        knownRootstocks: [
            "B.9",
            "B.118",
            "G.11",
            "G.30",
            "G.41",
            "G.202",
            "G.210",
            "G.214",
            "G.222",
            "G.890",
            "G.935",
            "G.969",
            "M.7",
            "M.9",
            "M.26",
            "MM.106",
            "MM.111",
            "Marubakaido",
        ],
        hasClonalSelection: true,
        defaultColor: "#ba3f3f",
        customName: false,
    },
    cherry: {
        hasRootstock: true,
        knownRootstocks: [
            "CAB-6P",
            "Colt",
            "Gisela 3",
            "Gisela 5",
            "Gisela 6",
            "Gisela 12",
            "Krymsk 5",
            "Krymsk 6",
            "Mahaleb",
            "Maxma 14",
            "Mazzard",
            "SL 64",
        ],
        hasClonalSelection: true,
        defaultColor: "#cf4d67",
        customName: false,
    },
    pistachio: {
        hasRootstock: true,
        knownRootstocks: ["Atlantica", "PG I", "PG II", "Platinum", "UCB1"],
        hasClonalSelection: false,
        defaultColor: "#67a457",
        customName: false,
    },
    // Almond, walnut and hazelnut take shell browns so nut blocks differ from berry blocks.
    almond: {
        hasRootstock: true,
        knownRootstocks: [
            "Atlas",
            "Bright’s Hybrid 5",
            "Cornerstone",
            "Garnem",
            "GF 677",
            "Hansen 536",
            "Krymsk 86",
            "Lovell",
            "Marianna 2624",
            "Nemaguard",
            "Nickels",
            "Rootpac 20",
            "Rootpac 40",
            "Rootpac R",
            "Titan",
            "Viking",
        ],
        hasClonalSelection: false,
        defaultColor: "#9a7152",
        customName: false,
    },
    walnut: {
        hasRootstock: true,
        knownRootstocks: ["Northern California black", "Paradox", "RX1", "Vlach", "VX211"],
        hasClonalSelection: false,
        defaultColor: "#6b4f3a",
        customName: false,
    },
    // Grown on its own roots, as nearly all orchards are; suckers rather than a graft.
    hazelnut: {
        hasRootstock: false,
        knownRootstocks: [],
        hasClonalSelection: false,
        defaultColor: "#8a5a3b",
        customName: false,
    },
    blueberry: {
        hasRootstock: false,
        knownRootstocks: [],
        hasClonalSelection: false,
        defaultColor: "#5974ad",
        customName: false,
    },
    blackberry: {
        hasRootstock: false,
        knownRootstocks: [],
        hasClonalSelection: false,
        defaultColor: "#554779",
        customName: false,
    },
    // Blackberry–raspberry hybrids.
    boysenberry: {
        hasRootstock: false,
        knownRootstocks: [],
        hasClonalSelection: false,
        defaultColor: "#864a72",
        customName: false,
    },
    loganberry: {
        hasRootstock: false,
        knownRootstocks: [],
        hasClonalSelection: false,
        defaultColor: "#a95262",
        customName: false,
    },
    tayberry: {
        hasRootstock: false,
        knownRootstocks: [],
        hasClonalSelection: false,
        defaultColor: "#bd5f72",
        customName: false,
    },
    raspberry: {
        hasRootstock: false,
        knownRootstocks: [],
        hasClonalSelection: false,
        defaultColor: "#c94c6e",
        customName: false,
    },
    strawberry: {
        hasRootstock: false,
        knownRootstocks: [],
        hasClonalSelection: false,
        defaultColor: "#e04f62",
        customName: false,
    },
    citrus: {
        hasRootstock: true,
        knownRootstocks: [
            "Bitters",
            "C-35",
            "Carrizo",
            "Cleopatra Mandarin",
            "Flying Dragon",
            "Macrophylla",
            "Rangpur Lime",
            "Rough Lemon",
            "Sour Orange",
            "Sunki Mandarin",
            "Swingle",
            "Trifoliate Orange",
            "Troyer",
            "US-942",
            "Volkameriana",
        ],
        hasClonalSelection: true,
        defaultColor: "#e28b2e",
        customName: false,
    },
    avocado: {
        hasRootstock: true,
        knownRootstocks: [
            "Dusa",
            "Duke 7",
            "Toro Canyon",
            "Thomas",
            "Velvick",
            "Zentmyer",
            "Mexícola",
            "Topa Topa",
            "Zutano",
        ],
        hasClonalSelection: false,
        defaultColor: "#6f9e4f",
        customName: false,
    },
    custom: {
        hasRootstock: true,
        knownRootstocks: [],
        hasClonalSelection: true,
        defaultColor: "#55765d",
        customName: true,
    },
} as const satisfies Record<string, CropConfiguration>;

export type CropType = keyof typeof CropConfigurations;

/** Every `CropConfigurations` key, in declaration order. */
export const CROP_TYPES = Object.keys(CropConfigurations) as readonly CropType[];

/** Runtime guard for a crop type arriving as a string; ignores inherited prototype keys. */
export function isCropType(value: string): value is CropType {
    return Object.prototype.hasOwnProperty.call(CropConfigurations, value);
}
